import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  RefreshCw,
  Package,
  Users,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  UserCog,
  UserCircle2,
  Library,
  CreditCard,
  Receipt,
  BarChart3,
  Mail,
  Sparkles,
} from "lucide-react";

export type ModuleCard = {
  id: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  points: string[];
  primaryLink: { label: string; to: string };
  docLink?: { label: string; to: string };
  accent: "blue" | "emerald" | "amber" | "violet" | "rose" | "slate";
};

export type RoleCard = {
  id: string;
  icon: LucideIcon;
  title: string;
  whoItIs: string;
  canDo: string[];
  startLink: { label: string; to: string };
};

export type GettingStartedStep = {
  number: number;
  title: string;
  description: string;
  link?: { label: string; to: string };
};

export type QuickAnswer = {
  question: string;
  answer: string;
  link?: { label: string; to: string };
};

export const OVERVIEW_MODULES: ModuleCard[] = [
  {
    id: "catalog",
    icon: Library,
    title: "Catalog",
    tagline: "The books, journals, and digital resources your library owns.",
    points: [
      "Add titles by ISBN, manual entry, or bulk upload",
      "Track physical copies, editions, and conditions",
      "Publish digital resources (PDF, links, media)",
    ],
    primaryLink: { label: "Open catalog", to: "/catalog" },
    docLink: { label: "Catalog guide", to: "/documentation/catalog" },
    accent: "blue",
  },
  {
    id: "circulation",
    icon: RefreshCw,
    title: "Circulation",
    tagline: "Check out, return, renew, and place holds on items.",
    points: [
      "Fast checkout and return at the circulation desk",
      "Manage holds, overdues, and renewals",
      "Automatic fine calculation based on your policies",
    ],
    primaryLink: { label: "Open circulation", to: "/circulation" },
    docLink: { label: "Circulation guide", to: "/documentation/circulation" },
    accent: "emerald",
  },
  {
    id: "acquisitions",
    icon: Package,
    title: "Acquisitions",
    tagline: "Buy new materials — from request to order to invoice.",
    points: [
      "Collect patron recommendations and staff requests",
      "Send purchase orders to vendors and track receiving",
      "Match invoices to orders and keep fund balances accurate",
    ],
    primaryLink: { label: "Open acquisitions", to: "/acquisitions/requests" },
    docLink: { label: "Acquisitions guide", to: "/documentation/acquisition" },
    accent: "violet",
  },
  {
    id: "patrons",
    icon: Users,
    title: "Patrons & cards",
    tagline: "Members of your library and the cards they use.",
    points: [
      "Approve new registrations and collect fees",
      "Issue virtual and printed library cards",
      "Track membership expiry and patron history",
    ],
    primaryLink: { label: "Open patrons", to: "/patrons" },
    accent: "amber",
  },
  {
    id: "reports",
    icon: BarChart3,
    title: "Reports & insights",
    tagline: "Understand how your library is being used.",
    points: [
      "Dashboards for checkouts, returns, and overdues",
      "Fine collection and invoice reporting",
      "Registration and patron activity logs",
    ],
    primaryLink: { label: "Open dashboard", to: "/dashboard" },
    accent: "rose",
  },
  {
    id: "settings",
    icon: Settings,
    title: "Library settings",
    tagline: "Configure your library exactly how you run it.",
    points: [
      "Branches, locations, item types, and collections",
      "Patron types, circulation policies, and fine rules",
      "Branding, email templates, and staff roles",
    ],
    primaryLink: { label: "Open setup", to: "/setup" },
    accent: "slate",
  },
];

export const OVERVIEW_ROLES: RoleCard[] = [
  {
    id: "admin",
    icon: ShieldCheck,
    title: "Administrator",
    whoItIs: "You own or run the library and set things up for everyone else.",
    canDo: [
      "Configure branches, policies, and branding",
      "Manage staff accounts, roles, and permissions",
      "Oversee budgets, vendors, and acquisitions",
    ],
    startLink: { label: "Admin guide", to: "/documentation/acquisition/admin" },
  },
  {
    id: "staff",
    icon: UserCog,
    title: "Library staff",
    whoItIs: "You work the desk, catalog materials, or handle acquisitions.",
    canDo: [
      "Check items in and out, manage holds and fines",
      "Catalog new titles and process received orders",
      "Approve patron registrations and issue cards",
    ],
    startLink: { label: "Staff guide", to: "/documentation/acquisition/staff" },
  },
  {
    id: "patron",
    icon: UserCircle2,
    title: "Patron",
    whoItIs: "You're a library member borrowing and reading items.",
    canDo: [
      "Browse the catalog and place holds",
      "See your checkouts, due dates, and fines",
      "Recommend titles for the library to purchase",
    ],
    startLink: { label: "Patron guide", to: "/documentation/acquisition/patron" },
  },
];

export const GETTING_STARTED_STEPS: GettingStartedStep[] = [
  {
    number: 1,
    title: "Set up your library",
    description:
      "Add your branches, locations, and basic branding so PhiloLMS feels like your library.",
    link: { label: "Go to setup", to: "/setup" },
  },
  {
    number: 2,
    title: "Define your policies",
    description:
      "Choose patron types, item types, loan periods, and fine rules. These power circulation and registration.",
    link: { label: "Circulation policies", to: "/setup/circulation-policies" },
  },
  {
    number: 3,
    title: "Invite your staff",
    description:
      "Create roles, assign permissions, and send invites so your team can start working right away.",
    link: { label: "Manage staff", to: "/setup/staff" },
  },
  {
    number: 4,
    title: "Build your catalog",
    description:
      "Add your first titles by ISBN, manual entry, or bulk import. Attach physical copies or digital files.",
    link: { label: "Open catalog", to: "/catalog" },
  },
  {
    number: 5,
    title: "Open your doors",
    description:
      "Accept patron registrations, issue cards, and start checking books in and out at the desk.",
    link: { label: "Circulation desk", to: "/circulation" },
  },
];

export const QUICK_ANSWERS: QuickAnswer[] = [
  {
    question: "How do I add a new book?",
    answer:
      "Open Catalog, then choose Add by ISBN for fast metadata lookup, or Manual entry for custom records. You can also bulk-import a spreadsheet.",
    link: { label: "Add by ISBN", to: "/catalog/isbn" },
  },
  {
    question: "A member wants to borrow a book — what do I do?",
    answer:
      "Go to Circulation, scan or search the patron's card, then scan each item barcode. PhiloLMS applies your loan period and fine rules automatically.",
    link: { label: "Open circulation", to: "/circulation" },
  },
  {
    question: "A patron suggested a book we don't own",
    answer:
      "Patron recommendations land in Acquisitions → Requests. Review, approve, and turn approved requests into a purchase order to a vendor.",
    link: { label: "Requests inbox", to: "/acquisitions/requests" },
  },
  {
    question: "How do I create a staff account?",
    answer:
      "In Setup → Staff, add a new member, pick a role (which controls what they can see and do), and send them an invitation email.",
    link: { label: "Manage staff", to: "/setup/staff" },
  },
  {
    question: "Where do I see overdue items or outstanding fines?",
    answer:
      "Circulation has dedicated Overdue and Holds views. Fines has its own page for collection, waiving, and patron history.",
    link: { label: "Overdue items", to: "/circulation/overdue" },
  },
  {
    question: "How do patrons get a library card?",
    answer:
      "They register from your tenant landing page, pay any required fee, and staff approve them. Cards can be virtual (QR) or printed in batches.",
    link: { label: "Patron registrations", to: "/admin/registrations" },
  },
];

export const OVERVIEW_SHORTCUTS: { icon: LucideIcon; label: string; to: string }[] = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/dashboard" },
  { icon: BookOpen, label: "Catalog", to: "/catalog" },
  { icon: RefreshCw, label: "Circulation", to: "/circulation" },
  { icon: Package, label: "Acquisitions", to: "/acquisitions/requests" },
  { icon: CreditCard, label: "Cards", to: "/admin/cards" },
  { icon: Receipt, label: "Invoices", to: "/invoices" },
];

export const OVERVIEW_HIGHLIGHTS = {
  title: "What is PhiloLMS?",
  icon: Sparkles,
  paragraphs: [
    "PhiloLMS is a modern, multi-tenant Library Management System. It helps libraries of any size catalog their collections, circulate materials, manage memberships, and order new books — all from one place.",
    "This documentation is your launchpad. Start with your role below, then dive into the module that matches what you're trying to do today.",
  ],
} as const;

export const CONTACT_SUPPORT = {
  icon: Mail,
  title: "Still stuck?",
  description:
    "If something in PhiloLMS isn't behaving the way you expect, send us a message and we'll help you sort it out.",
  cta: { label: "Contact support", to: "/contact" },
} as const;
